import React, { useRef, useState, useEffect } from 'react';
import { useIntl, FormattedMessage } from '../../../util/reactIntl';
import { formatMoney } from '../../../util/currency';
import { NamedLink } from '../../../components';
import styles from './SectionPromotedListings.module.css';

const getListingImageUrl = listing => {
  const images = listing?.images;
  if (!images || images.length === 0) return null;
  const image = images[0];
  return (
    image?.attributes?.variants?.['listing-card']?.url ||
    image?.attributes?.variants?.['scaled-small']?.url ||
    null
  );
};

const PromotedCard = ({ listing, intl }) => {
  const { id, attributes } = listing;
  const { title, price, publicData } = attributes;
  const customUnitType = publicData?.unit_type || '';
  const showUnit = customUnitType && customUnitType !== 'fixed' && customUnitType !== 'item';
  const imageUrl = getListingImageUrl(listing);

  const priceFormatted = price ? formatMoney(intl, price) : null;
  const priceLabel = priceFormatted
    ? showUnit
    ? `${priceFormatted} / ${customUnitType}`
    : priceFormatted
    : null;

  return (
    <NamedLink
      name="ListingPage"
      params={{ id: id.uuid, slug: encodeURIComponent(title) }}
      className={styles.promotedCard}
    >
      <div className={styles.imageContainer}>
        {imageUrl ? (
          <img src={imageUrl} alt={title} className={styles.promotedImage} />
        ) : (
          <div className={styles.imagePlaceholder} />
        )}
        <div className={styles.cardBadge}>
          <span className={styles.cardBadgeDot} />
          <FormattedMessage id="SectionPromotedListings.cardBadge" />
        </div>
      </div>
      <div className={styles.promotedInfo}>
        <h3 className={styles.promotedName}>{title}</h3>
        {priceLabel && <p className={styles.price}>{priceLabel}</p>}
      </div>
    </NamedLink>
  );
};

const SkeletonCard = () => (
  <div className={styles.promotedCard}>
    <div className={`${styles.imageContainer} ${styles.skeleton}`} />
    <div className={styles.promotedInfo}>
      <div className={`${styles.skeletonText} ${styles.skeletonTitle}`} />
      <div className={`${styles.skeletonText} ${styles.skeletonPrice}`} />
    </div>
  </div>
);

/**
 * Promoted Listings section for the Landing Page.
 * Displays listings that have been designated as promoted by the admin in a carousel slider.
 */
const SectionPromotedListings = ({ listings = [], inProgress = false }) => {
  const intl = useIntl();
  const sliderRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);
  const showSkeletons = inProgress && listings.length === 0;
  const tripledListings = !inProgress && listings.length > 1 ? [...listings, ...listings, ...listings] : listings;

  useEffect(() => {
    if (listings.length <= 1 || inProgress || isPaused) return;

    const autoScroll = () => {
      const slider = sliderRef.current;
      if (!slider) return;

      const card = slider.firstElementChild;
      const cardWidth = card?.clientWidth || 282;
      const step = cardWidth + 24;

      slider.scrollLeft += step;
    };

    const intervalId = setInterval(autoScroll, 4000);

    return () => {
      clearInterval(intervalId);
    };
  }, [listings, inProgress, isPaused]);

  // Initial scroll position setup for infinite scroll
  useEffect(() => {
    if (listings.length <= 1 || inProgress) return;

    const initScroll = () => {
      const slider = sliderRef.current;
      if (!slider) return;

      const card = slider.firstElementChild;
      const cardWidth = card?.clientWidth || 282;
      const gap = 24;
      const oneSetWidth = listings.length * (cardWidth + gap);

      slider.style.scrollBehavior = 'auto';
      slider.scrollLeft = oneSetWidth;
      requestAnimationFrame(() => {
        if (slider) slider.style.scrollBehavior = '';
      });
    };

    const timerId = setTimeout(initScroll, 100);
    return () => clearTimeout(timerId);
  }, [listings, inProgress]);

  const handleScroll = () => {
    const slider = sliderRef.current;
    if (!slider || listings.length <= 1 || inProgress) return;

    const card = slider.firstElementChild;
    const cardWidth = card?.clientWidth || 282;
    const gap = 24;
    const oneSetWidth = listings.length * (cardWidth + gap);

    if (slider.scrollLeft >= oneSetWidth * 2) {
      slider.style.scrollBehavior = 'auto';
      slider.scrollLeft -= oneSetWidth;
      // Force repaint to make the position snap instantly and invisibly
      slider.offsetHeight;
      slider.style.scrollBehavior = '';
    } else if (slider.scrollLeft <= oneSetWidth * 0.5) {
      slider.style.scrollBehavior = 'auto';
      slider.scrollLeft += oneSetWidth;
      // Force repaint to make the position snap instantly and invisibly
      slider.offsetHeight;
      slider.style.scrollBehavior = '';
    }
  };

  // If loading is finished and there are no promoted listings, don't render this section
  if (!inProgress && listings.length === 0) {
    return null;
  }

  const scrollLeft = e => {
    const slider = sliderRef.current;
    if (!slider) return;
    const card = slider.firstElementChild;
    const cardWidth = card?.clientWidth || 282;
    // Slide by 2 cards (including 24px gap)
    slider.scrollLeft -= (cardWidth + 24) * 2;
    e.currentTarget.blur();
  };

  const scrollRight = e => {
    const slider = sliderRef.current;
    if (!slider) return;
    const card = slider.firstElementChild;
    const cardWidth = card?.clientWidth || 282;
    // Slide by 2 cards (including 24px gap)
    slider.scrollLeft += (cardWidth + 24) * 2;
    e.currentTarget.blur();
  };

  return (
    <section className={styles.promotedSection}>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.titleWrapper}>
            <span className={styles.badge}>
              <FormattedMessage id="SectionPromotedListings.badge" />
            </span>
            <h2 className={styles.title}>
              <FormattedMessage id="SectionPromotedListings.title" />
            </h2>
            <p className={styles.subtitle}>
              <FormattedMessage id="SectionPromotedListings.subtitle" />
            </p>
          </div>
        </div>

        <div
          className={styles.carouselContainer}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {listings.length > 1 && (
            <>
              <button
                className={`${styles.carouselArrow} ${styles.prev}`}
                onClick={scrollLeft}
                aria-label="Previous items"
              >
                ‹
              </button>
              <button
                className={`${styles.carouselArrow} ${styles.next}`}
                onClick={scrollRight}
                aria-label="Next items"
              >
                ›
              </button>
            </>
          )}

          <ul
            className={styles.promotedSlider}
            ref={sliderRef}
            onScroll={handleScroll}
          >
            {showSkeletons
              ? Array.from({ length: 4 }).map((_, i) => (
                  <li key={i} className={styles.listItem}>
                    <SkeletonCard />
                  </li>
                ))
              : tripledListings.map((listing, index) => (
                  <li key={`${listing.id.uuid}-${index}`} className={styles.listItem}>
                    <PromotedCard listing={listing} intl={intl} />
                  </li>
                ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default SectionPromotedListings;
